/**
 * The music itself, as data.
 *
 * Every piece here is original. What is borrowed is the genre grammar — slow
 * minor loops, major-seventh warmth, a hymn cadence, a melody made of long held
 * notes with more rest than note between them — which is the part that is common
 * property. Nothing in this file transcribes, quotes or approximates any
 * existing recording, and the game ships no audio files.
 *
 * A `PieceSpec` is a key, a tempo, a chord loop, a scale for the melody to
 * wander around in, and an instrument profile. One `PiecePlayer` renders any
 * of them, so a new piece is a literal in the table below rather than a new
 * function. The player is not fully determined by the spec: unless a piece
 * carries an explicit `contour`, the melody is improvised against the chord loop
 * from the run's RNG, so hearing the same piece twice in a session is not
 * hearing the same performance.
 *
 * Registers are chosen with the wall filter in `tape.ts` in mind. A voicing
 * spread across two octaves above a low root arrives as its bottom two notes
 * and a suggestion of the rest, which is what a chord sounds like through a
 * partition — so the pieces are voiced wide on purpose, and every interval that
 * is *meant* to be heard as wrong is placed in the bottom two octaves where the
 * wall will still pass both of its notes.
 *
 * The set is deliberately not uniform in mood. `daylight` is only sad; it is
 * there so the rest have something to be wrong against.
 */

import type { RNG } from '../core/types';
import type { AudioGraph } from './graph';
import { makeHarmonicWave } from './noise';
import type { TapeChain, TapeProfile } from './tape';

/**
 * How a note is made.
 *
 *   stack   detuned oscillators through a low-pass. Stands in for a section of
 *           strings that has been through a tape machine too many times.
 *   fm      sine carrier bent by a sine modulator whose depth decays. The
 *           cheapest convincing electric piano, and with a high ratio and a
 *           fast decay, also a music box.
 *   organ   one oscillator on a built harmonic series. Reeds and choirs.
 */
export type VoiceKind = 'stack' | 'fm' | 'organ';

export interface VoiceProfile {
  kind: VoiceKind;
  /** stack: waveform and how many copies, spread this many cents either side. */
  wave?: OscillatorType;
  unison?: number;
  spread?: number;
  /** fm: modulator frequency as a multiple of the carrier. */
  ratio?: number;
  /** fm: peak modulation depth, as a multiple of the carrier frequency. */
  index?: number;
  /** fm: seconds for the modulation to fall away. Short is a strike. */
  modDecay?: number;
  /** organ: partial amplitudes from the fundamental up. */
  partials?: readonly number[];
  /**
   * Seconds the note spends rising out of nothing *before* its own attack, or
   * omitted for a note that simply starts.
   *
   * This is a tape played backwards: the swell arrives first and the transient
   * lands at the end of it, so the note is already there by the time it begins.
   * The peak lands `swell` seconds after the beat rather than on it, which is
   * left uncorrected — a whole piece hitting late is more use than a piece that
   * has been quietly nudged early to hide it.
   */
  swell?: number;
  /** Seconds. A slow attack is what stops any of this reading as a synth stab. */
  attack: number;
  decay: number;
  /** Fraction of peak held after the decay. */
  sustain: number;
  release: number;
  /** Per-voice low-pass in Hz, before the wall filter gets to it. */
  tone: number;
  /**
   * Constant mistuning for this voice in cents, against everything else in the
   * piece. A whole instrument that is a third of a semitone sharp of the one
   * underneath it is much harder to dismiss than a dissonant chord, because
   * there is nothing to blame it on.
   */
  offset?: number;
}

export interface MelodySpec {
  voice: VoiceProfile;
  /** Semitones above the root the melody may use. */
  scale: readonly number[];
  /** Chance that a given beat carries a note at all. Most of these are low. */
  density: number;
  /** Note length in beats. */
  hold: number;
  /** Octaves above the root. */
  octave: number;
  level: number;
  /**
   * How strongly an improvised note is pulled onto a tone of the current chord,
   * 0..1. The default keeps a scale walk inside the harmony; low values let it
   * sit on the notes between, which is where the piece stops sounding like it
   * knows what its own chords are.
   */
  pull?: number;
  /**
   * An explicit tune, one entry per beat, in semitones above the melody's own
   * root, `null` for a rest. Looped, and read by bar index rather than by a
   * counter, so a bar the scheduler skipped does not shift the tune.
   *
   * A written contour ignores `density` and `pull` entirely. It exists for the
   * one piece that has to play an actual tune: a nursery shape played straight
   * on something slightly out of tune is worse than any amount of dissonance,
   * and improvisation cannot produce a shape a player recognises as a tune.
   */
  contour?: readonly (number | null)[];
}

export interface PieceSpec {
  id: string;
  /** One line on what this is meant to be. */
  about: string;
  /** MIDI note of the tonic. */
  root: number;
  bpm: number;
  /** Beats per chord. One chord is one bar here; there are no other bars. */
  beatsPerChord: number;
  /** The loop, each entry a voicing in semitones relative to the root. */
  progression: ReadonlyArray<readonly number[]>;
  pad: VoiceProfile;
  padLevel: number;
  /** Octaves above the root for the pad voicing. */
  padOctave: number;
  /** A held root under the chord, or null. */
  bass: { voice: VoiceProfile; level: number; octave: number } | null;
  melody: MelodySpec | null;
  tape: TapeProfile;
  /**
   * Cents of mistuning per pitch class, from C up, or omitted for an instrument
   * that is in tune with itself.
   *
   * Keyed to absolute pitch class rather than to the tonic, so the piece's
   * per-performance transposition moves the tonic around inside a fixed pattern
   * of bad notes. That is how a real instrument nobody has tuned behaves: it is
   * not sour in a key, it is sour on particular notes.
   */
  temper?: readonly number[];
  /**
   * Chance that this performance simply stops partway through and does not come
   * back. The surface noise runs on for a few seconds afterwards, so what the
   * player hears is the source being interrupted rather than the layer fading.
   */
  abandon?: number;
  /**
   * Overall trim, set by measurement rather than by ear: each piece is rendered
   * alone through an `OfflineAudioContext` and its gated loudness matched to the
   * rest of the set, so no piece is a step up or down from the one before it.
   * They are not all 1 because the wall filter takes a very different amount
   * away from a piece voiced at 260Hz than from one voiced at 950Hz. Re-derive
   * these rather than nudging them if a piece's voicing or tape profile changes.
   */
  gain: number;
  /** Seconds. The scheduler draws the length of a performance from this range. */
  minSeconds: number;
  maxSeconds: number;
}

/* ------------------------------------------------------------------ *
 * Instruments
 * ------------------------------------------------------------------ */

/**
 * A string section that has been copied one generation too many.
 *
 * The spread is wider than is comfortable on purpose. Three saws ten cents
 * either side of the note beat against each other at well under a hertz down
 * here, so a held chord never quite settles — it is the sound of a section that
 * has been holding the same note for slightly too long.
 */
const TAPE_STRINGS: VoiceProfile = {
  kind: 'stack',
  wave: 'sawtooth',
  unison: 3,
  spread: 21,
  attack: 1.1,
  decay: 2.4,
  sustain: 0.72,
  release: 3.6,
  tone: 900,
};

/** Softer and rounder — a dance band's brass heard as a shape rather than a tone. */
const TAPE_BRASS: VoiceProfile = {
  kind: 'stack',
  wave: 'triangle',
  unison: 4,
  spread: 19,
  attack: 0.7,
  decay: 1.8,
  sustain: 0.66,
  release: 2.8,
  tone: 1100,
};

/** Barely a note. Used where the piece is mostly the room. */
const GLASS_PAD: VoiceProfile = {
  kind: 'stack',
  wave: 'sine',
  unison: 3,
  spread: 16,
  attack: 4.5,
  decay: 5,
  sustain: 0.85,
  release: 7,
  tone: 1400,
};

/**
 * The same section, running backwards. Every note fades up out of nothing for
 * two and a half seconds and then strikes, so a chord change is announced by
 * the chord that is about to happen.
 */
const REVERSED_STRINGS: VoiceProfile = {
  kind: 'stack',
  wave: 'sawtooth',
  unison: 3,
  spread: 23,
  swell: 2.6,
  attack: 0.28,
  decay: 3.2,
  sustain: 0.46,
  release: 4.4,
  tone: 820,
};

/** A single reversed tone, for the pieces that are nearly silence. */
const REVERSED_GLASS: VoiceProfile = {
  kind: 'stack',
  wave: 'sine',
  unison: 2,
  spread: 18,
  swell: 3.4,
  attack: 0.2,
  decay: 4,
  sustain: 0.3,
  release: 6,
  tone: 1300,
};

/** Gentle FM electric piano. Low index, so it stays a tone and never a bell. */
const RHODES: VoiceProfile = {
  kind: 'fm',
  ratio: 2,
  index: 1.5,
  modDecay: 0.5,
  attack: 0.012,
  decay: 2.2,
  sustain: 0.28,
  release: 2.6,
  tone: 1600,
};

/**
 * Same synthesis, struck harder and let go faster: a music box two rooms away,
 * a third of a semitone sharp of everything underneath it. The offset is the
 * whole point of the instrument and is not a value to round off — at 30 cents
 * it is plainly wrong, and at 10 it just sounds like a bad recording.
 */
const BOX_BELL: VoiceProfile = {
  kind: 'fm',
  ratio: 3.5,
  index: 3.2,
  modDecay: 0.22,
  attack: 0.006,
  decay: 1.6,
  sustain: 0.06,
  release: 2.2,
  tone: 1900,
  offset: 31,
};

/** Reed organ. The partials do the work; the envelope is almost square. */
const REED_ORGAN: VoiceProfile = {
  kind: 'organ',
  partials: [1, 0.5, 0.62, 0.2, 0.28, 0.1, 0.07, 0.05],
  attack: 0.55,
  decay: 1.2,
  sustain: 0.88,
  release: 1.9,
  tone: 1200,
};

/** Nothing but a fundamental, for the note under everything. */
const SUB_TONE: VoiceProfile = {
  kind: 'stack',
  wave: 'sine',
  unison: 1,
  spread: 0,
  attack: 1.4,
  decay: 2,
  sustain: 0.8,
  release: 3.4,
  tone: 320,
};

/* ------------------------------------------------------------------ *
 * The pieces
 * ------------------------------------------------------------------ */

/**
 * Eight of them. The scheduler will not repeat a piece back to back, so a long
 * session moves around the set.
 *
 * Each one is wrong in exactly one way and otherwise plays it straight, because
 * a piece that is wrong in four ways at once reads as a sound effect. `daylight`
 * is not wrong at all — it is the control, and the others only land because it
 * exists.
 */
export const PIECES: readonly PieceSpec[] = [
  {
    id: 'waltz-for-nobody',
    about: 'Slow minor waltz through a floor. The strings beat; the cadence goes the wrong way.',
    root: 45, // A2
    bpm: 84,
    beatsPerChord: 3,
    progression: [
      [0, 7, 12, 15], // Am
      [5, 12, 17, 20], // Dm
      [3, 10, 14, 15], // Cmaj7, voiced closed so the B sits under the C
      // Where the turnaround should be. An Eb7 over a held A is a tritone in
      // the bass and does not go back to A minor, and it goes back anyway.
      [6, 10, 13, 17],
    ],
    pad: TAPE_STRINGS,
    padLevel: 0.15,
    padOctave: 0,
    bass: { voice: SUB_TONE, level: 0.2, octave: -1 },
    melody: {
      voice: TAPE_BRASS,
      scale: [0, 3, 5, 6, 7, 8, 10, 12, 15],
      density: 0.2,
      hold: 2.2,
      octave: 1,
      level: 0.11,
      pull: 0.3,
    },
    tape: {
      wall: 380,
      wow: 12,
      drift: 22,
      wowRate: 0.62,
      surface: 0.02,
      crackle: 0.05,
      dropoutEvery: 42,
      sag: 24,
      sagEvery: 19,
      send: 0.72,
    },
    gain: 0.656,
    minSeconds: 75,
    maxSeconds: 130,
  },
  {
    id: 'last-dance',
    about: 'Four to the bar and much too slow. The tape loses pitch across the piece and never gets it back.',
    root: 41, // F2
    bpm: 52,
    beatsPerChord: 4,
    progression: [
      [0, 7, 12, 16], // F
      [10, 14, 17, 22], // Eb
      [3, 10, 14, 15], // Abmaj7 closed
      [8, 12, 15, 19], // Dbmaj7
      [5, 12, 17, 21], // Bb
      // A semitone above home, arrived at from below and left downwards. The
      // held F underneath it is what makes it sound like a mistake rather than
      // a modulation.
      [1, 8, 13, 17],
    ],
    pad: TAPE_BRASS,
    padLevel: 0.14,
    padOctave: 0,
    bass: { voice: SUB_TONE, level: 0.22, octave: -1 },
    melody: {
      voice: TAPE_STRINGS,
      scale: [0, 2, 3, 5, 7, 10, 12, 14, 15],
      density: 0.15,
      hold: 3,
      octave: 1,
      level: 0.1,
      pull: 0.35,
    },
    tape: {
      // The most degraded of the set: barely any top, and the wow is wide
      // enough to be the point rather than a texture.
      wall: 265,
      wow: 20,
      drift: 34,
      wowRate: 0.38,
      surface: 0.028,
      crackle: 0.07,
      dropoutEvery: 24,
      sag: 44,
      sagEvery: 15,
      send: 0.8,
    },
    abandon: 0.35,
    gain: 1.079,
    minSeconds: 90,
    maxSeconds: 150,
  },
  {
    id: 'empty-hall',
    about: 'The same band with the melody gone, and the tape on backwards — chords that arrive before they start.',
    root: 43, // G2
    bpm: 44,
    beatsPerChord: 6,
    progression: [
      [0, 3, 10, 14], // Gm with the 9th on top
      [8, 12, 15, 19], // Ebmaj7
      [1, 8, 13, 15], // Ab over a held G
      [10, 13, 14, 17], // F with both the Ab and the A in it
    ],
    pad: REVERSED_STRINGS,
    padLevel: 0.15,
    padOctave: 0,
    bass: { voice: SUB_TONE, level: 0.18, octave: -1 },
    melody: {
      voice: REVERSED_GLASS,
      scale: [0, 1, 3, 7, 10, 12],
      density: 0.06,
      hold: 4,
      octave: 1,
      level: 0.08,
      pull: 0.15,
    },
    tape: {
      wall: 320,
      wow: 14,
      drift: 26,
      wowRate: 0.35,
      surface: 0.024,
      crackle: 0.055,
      dropoutEvery: 34,
      sag: 18,
      sagEvery: 26,
      send: 0.85,
    },
    gain: 1.337,
    minSeconds: 100,
    maxSeconds: 150,
  },
  {
    id: 'daylight',
    about: 'Electric piano, major sevenths, a small figure going round. The one that is only sad.',
    root: 48, // C3
    bpm: 64,
    beatsPerChord: 4,
    progression: [
      [-12, 0, 7, 11, 16],
      [-3, 4, 9, 14, 19],
      [-7, 5, 9, 12, 16],
      [-5, 7, 11, 14, 19],
    ],
    pad: RHODES,
    padLevel: 0.1,
    padOctave: 0,
    bass: { voice: SUB_TONE, level: 0.17, octave: -2 },
    melody: {
      voice: RHODES,
      scale: [0, 2, 4, 7, 9, 11, 12, 14, 16],
      density: 0.34,
      hold: 1.4,
      octave: 1,
      level: 0.085,
    },
    tape: {
      // Open enough to hear what it is, which is what makes it warm rather
      // than eerie. The hiss carries the nostalgia instead of the filter.
      // No sag and no dropouts: this is the copy that was looked after.
      wall: 1050,
      wow: 7,
      drift: 12,
      wowRate: 0.78,
      surface: 0.036,
      crackle: 0.022,
      dropoutEvery: 55,
      send: 0.55,
    },
    gain: 0.969,
    minSeconds: 70,
    maxSeconds: 120,
  },
  {
    id: 'long-sunday',
    about: 'One chord every thirteen seconds. Slow enough that it stops being a tempo and becomes weather.',
    root: 38, // D2
    bpm: 19,
    beatsPerChord: 4,
    progression: [
      [0, 7, 12, 16, 19], // D
      [-2, 5, 10, 13, 17], // Cm11
      [1, 8, 12, 13, 18], // Eb with the D still in it
      [-4, 3, 10, 14, 15], // Bb with the E rubbing the F
      [0, 6, 12, 18, 19], // D and Ab, and then the A arrives anyway
    ],
    pad: GLASS_PAD,
    padLevel: 0.062,
    padOctave: 1,
    bass: { voice: SUB_TONE, level: 0.2, octave: 0 },
    melody: {
      voice: RHODES,
      scale: [0, 1, 3, 6, 7, 10, 13],
      density: 0.07,
      hold: 6,
      octave: 2,
      level: 0.042,
      pull: 0.2,
    },
    tape: {
      wall: 700,
      wow: 17,
      drift: 32,
      wowRate: 0.24,
      surface: 0.03,
      crackle: 0.03,
      dropoutEvery: 48,
      sag: 30,
      sagEvery: 27,
      send: 0.95,
    },
    gain: 0.686,
    minSeconds: 100,
    maxSeconds: 155,
  },
  {
    id: 'two-chords',
    about: 'Two chords a semitone apart with one note that refuses to move. Long silences, almost no melody.',
    root: 40, // E2
    bpm: 34,
    beatsPerChord: 8,
    progression: [
      [0, 7, 14, 19],
      // Everything drops a semitone except the B, which stays and is now a
      // semitone off the top of the chord it is in.
      [-1, 7, 13, 18],
    ],
    pad: GLASS_PAD,
    padLevel: 0.125,
    padOctave: 0,
    bass: null,
    melody: {
      voice: REVERSED_GLASS,
      scale: [0, 1, 7, 12, 13],
      density: 0.035,
      hold: 7,
      octave: 2,
      level: 0.055,
      pull: 0.1,
    },
    tape: {
      wall: 430,
      wow: 9,
      drift: 22,
      wowRate: 0.29,
      surface: 0.018,
      crackle: 0.012,
      dropoutEvery: 0,
      sag: 16,
      sagEvery: 34,
      send: 0.9,
    },
    abandon: 0.22,
    gain: 0.945,
    minSeconds: 110,
    maxSeconds: 155,
  },
  {
    id: 'music-box',
    about: 'A music box someone else wound, playing a nursery tune straight, sharp of the room it is in.',
    root: 52, // E3
    bpm: 58,
    beatsPerChord: 4,
    progression: [
      [-12, 0, 3, 7], // Em
      [-10, 2, 5, 9], // F#m
      [-5, 0, 3, 8], // C over E — the B underneath the C
      [-7, -1, 2, 7], // B7, which does resolve, on the one thing in tune with nothing
    ],
    pad: REED_ORGAN,
    padLevel: 0.06,
    padOctave: -1,
    bass: { voice: SUB_TONE, level: 0.14, octave: -2 },
    melody: {
      voice: BOX_BELL,
      scale: [0, 3, 5, 7, 10, 12],
      // Four bars of four. Symmetrical, singable, and entirely unremarkable,
      // which is what makes the tuning impossible to ignore.
      contour: [0, 3, 7, 3, 5, 3, 2, null, 0, 3, 7, 12, 10, 7, 3, null],
      density: 1,
      hold: 1.1,
      octave: 1,
      level: 0.075,
    },
    tape: {
      wall: 950,
      wow: 14,
      drift: 24,
      wowRate: 0.55,
      surface: 0.015,
      crackle: 0.04,
      dropoutEvery: 38,
      sag: 22,
      sagEvery: 21,
      send: 0.88,
    },
    // Two notes of the comb have gone. Absolute pitch classes, so the piece's
    // per-performance transposition slides the tune through them.
    temper: [0, 0, 0, -23, 0, 0, 0, 0, 0, 15, 0, 0],
    abandon: 0.3,
    gain: 1.306,
    minSeconds: 80,
    maxSeconds: 130,
  },
  {
    id: 'stairwell-organ',
    about: 'A reed organ playing a hymn cadence, harmonised in major sevenths, landing a semitone above home.',
    root: 43, // G2
    bpm: 40,
    beatsPerChord: 4,
    progression: [
      // Every chord is a closed major seventh: correct four-part writing, and
      // no hymn has ever been harmonised this way.
      [0, 7, 11, 12], // Gmaj7
      [5, 12, 16, 17], // Cmaj7
      [2, 9, 13, 14], // Amaj7
      [7, 14, 18, 19], // Dmaj7 — the dominant, and it is about to be wasted
      [1, 8, 12, 13], // Abmaj7 over a held G, and then round again
    ],
    pad: REED_ORGAN,
    padLevel: 0.085,
    padOctave: 0,
    bass: { voice: SUB_TONE, level: 0.19, octave: -1 },
    melody: {
      voice: REED_ORGAN,
      scale: [0, 2, 3, 7, 11, 12, 14],
      density: 0.16,
      hold: 3,
      octave: 1,
      level: 0.07,
      pull: 0.2,
    },
    tape: {
      wall: 620,
      wow: 10,
      drift: 18,
      wowRate: 0.44,
      surface: 0.022,
      crackle: 0.045,
      dropoutEvery: 46,
      sag: 20,
      sagEvery: 24,
      send: 0.82,
    },
    gain: 0.815,
    minSeconds: 85,
    maxSeconds: 140,
  },
];

/* ------------------------------------------------------------------ *
 * The player
 * ------------------------------------------------------------------ */

/** Seconds of tail allowed past a note's release before its nodes are freed. */
const REAP_MARGIN = 0.08;
/** Bars placed per call. Bars are seconds long; this only bounds a catch-up. */
const MAX_BARS_PER_CALL = 4;

interface Note {
  sources: AudioScheduledSourceNode[];
  nodes: AudioNode[];
  /** Detune params the tape chain is driving, to be released with the note. */
  warbled: AudioParam[];
  endsAt: number;
}

/**
 * Renders one `PieceSpec` into the tape chain, a bar at a time, ahead of the
 * audio clock.
 *
 * Nothing here uses a JS timer. `schedule` is handed a window and places every
 * bar that starts inside it; the actual timing is entirely in the automation
 * it writes, so a dropped frame or a hitch in level generation cannot make the
 * music stutter. Notes are reaped by context time in `sweep`, which is also
 * what keeps a forty-minute session from accumulating oscillators.
 *
 * Every method that takes a time refuses a non-finite one rather than passing
 * it to an `AudioParam`, where it would throw and silently kill the layer.
 */
export class PiecePlayer {
  private readonly ctx: AudioContext;
  private readonly notes: Note[] = [];
  private readonly beat: number;
  private readonly barSeconds: number;
  private readonly transpose: number;
  /** Cached: a `PeriodicWave` per organ profile, built once instead of per note. */
  private readonly waves = new Map<VoiceProfile, PeriodicWave>();

  private barIndex = 0;
  private barAt: number;
  private stopAt = Infinity;
  /** Index into the melody scale, so phrases move by step more often than not. */
  private degree: number;

  constructor(
    private readonly g: AudioGraph,
    readonly spec: PieceSpec,
    private readonly rng: RNG,
    private readonly tape: TapeChain,
    startAt: number,
  ) {
    this.ctx = g.ctx;
    // A few percent either way on tempo and a couple of semitones of key, so a
    // piece is recognisable between runs without being identical.
    this.beat = 60 / (spec.bpm * rng.range(0.94, 1.06));
    this.barSeconds = this.beat * spec.beatsPerChord;
    this.transpose = rng.int(-2, 2);
    this.barAt = Number.isFinite(startAt) ? startAt : g.now;
    this.degree = spec.melody ? rng.int(0, spec.melody.scale.length - 1) : 0;
  }

  /** Place every bar starting before `until`. Cheap and idempotent per bar. */
  schedule(now: number, until: number): void {
    if (!Number.isFinite(now) || !Number.isFinite(until)) return;
    for (let i = 0; i < MAX_BARS_PER_CALL; i++) {
      if (this.barAt >= until || this.barAt >= this.stopAt) return;
      // A bar whose start has already gone by was slept through — a hidden tab
      // keeps the audio clock running while `requestAnimationFrame` does not.
      // Scheduling it now would fire the whole backlog at once.
      if (this.barAt >= now - 0.05) this.bar(this.barIndex, this.barAt);
      this.barIndex++;
      this.barAt += this.barSeconds;
    }
  }

  /** Stop placing new bars. Everything already scheduled rings out. */
  stopAfter(t: number): void {
    if (Number.isFinite(t) && t < this.stopAt) this.stopAt = t;
  }

  /** Free notes whose release has finished. */
  sweep(now: number): void {
    if (!Number.isFinite(now)) return;
    for (let i = this.notes.length - 1; i >= 0; i--) {
      const n = this.notes[i]!;
      if (now < n.endsAt) continue;
      this.release(n, now);
      this.notes.splice(i, 1);
    }
  }

  dispose(): void {
    const t = this.g.now;
    for (const n of this.notes) this.release(n, t);
    this.notes.length = 0;
  }

  private release(n: Note, t: number): void {
    for (const s of n.sources) {
      try {
        s.stop(t);
      } catch {
        /* already finished */
      }
    }
    // Detach before disconnecting: the tape chain's warble bus holds a
    // reference to every param it drives, and that is the one edge that would
    // keep a finished note alive for the length of the piece.
    for (const p of n.warbled) this.tape.detach(p);
    for (const node of n.nodes) node.disconnect();
    n.sources.length = 0;
    n.nodes.length = 0;
    n.warbled.length = 0;
  }

  /** One chord of the loop, plus whatever melody lands over it. */
  private bar(index: number, at: number): void {
    const s = this.spec;
    const chord = s.progression[index % s.progression.length]!;
    const root = s.root + this.transpose;

    // Held slightly past the bar so consecutive chords overlap rather than
    // hand off — a gap between chords reads as a synthesiser playing, not as a
    // record with a room around it.
    const hold = this.barSeconds * 1.08;
    for (const semi of chord) {
      this.note(root + s.padOctave * 12 + semi, at + this.rng.range(0, 0.09), hold, s.padLevel, s.pad);
    }

    if (s.bass) {
      this.note(root + s.bass.octave * 12, at, hold, s.bass.level, s.bass.voice);
    }

    const m = s.melody;
    if (!m) return;
    const mechanical = m.contour !== undefined;
    for (let b = 0; b < s.beatsPerChord; b++) {
      let semi: number;
      if (m.contour) {
        // Indexed off the bar, not off a counter, so a skipped bar drops its
        // four notes instead of shifting the whole tune four beats late.
        const step = m.contour[(index * s.beatsPerChord + b) % m.contour.length];
        if (step === null || step === undefined) continue;
        semi = step;
      } else {
        if (!this.rng.chance(m.density)) continue;
        semi = this.nextMelodyNote(m, chord);
      }
      // Nudged off the beat: nothing about this should sound quantised. A box
      // driven by a cylinder gets much less of that than a person does, and
      // never gets ahead of itself — a negative nudge on the first beat of a bar
      // falls before the bar and is dropped by the guard below, which would take
      // a note out of a written tune at random.
      const jitter = mechanical ? this.rng.range(0, 0.03) : this.rng.range(-0.05, 0.09);
      const t = at + b * this.beat + jitter * this.beat;
      if (t < at) continue;
      const vary = mechanical ? this.rng.range(0.9, 1.05) : this.rng.range(0.75, 1.1);
      this.note(root + m.octave * 12 + semi, t, m.hold * this.beat, m.level * vary, m.voice);
    }
  }

  /**
   * Walk the scale rather than sample it. Steps are much more likely than
   * leaps, and the walk is pulled back toward a tone of the current chord, so
   * a handful of notes an hour still sounds like someone playing.
   */
  private nextMelodyNote(m: MelodySpec, chord: readonly number[]): number {
    const n = m.scale.length;
    const step = this.rng.chance(0.68) ? this.rng.int(-1, 1) : this.rng.int(-3, 3);
    let i = this.degree + step;
    if (i < 0) i = -i;
    if (i >= n) i = n - 1 - (i - n + 1);
    this.degree = Math.max(0, Math.min(n - 1, i));

    const pick = m.scale[this.degree]!;
    if (!this.rng.chance(m.pull ?? 0.55)) return pick;
    // Otherwise pull to the nearest chord tone by pitch class, staying in the
    // melody's own octave. This is what stops a scale walk drifting off the
    // harmony over a long piece.
    let best = pick;
    let bestGap = 12;
    for (const c of chord) {
      const up = ((((c - pick) % 12) + 12) % 12) as number;
      const shift = up <= 6 ? up : up - 12;
      if (Math.abs(shift) < bestGap) {
        bestGap = Math.abs(shift);
        best = pick + shift;
      }
    }
    return best;
  }

  /**
   * One note. Builds its own little chain, schedules an envelope on it, and
   * records everything so `sweep` can take it apart once it has rung out.
   */
  private note(midi: number, at: number, dur: number, level: number, v: VoiceProfile): void {
    if (!Number.isFinite(at) || !Number.isFinite(dur)) return;
    const ctx = this.ctx;
    const freq = mtof(midi);
    // The instrument's own mistuning plus whatever the piece's temperament does
    // to this pitch class. Both are in cents and both go on `detune`, so the
    // shared warble bus still sums on top of them.
    const cents = (v.offset ?? 0) + (this.spec.temper?.[((midi % 12) + 12) % 12] ?? 0);
    const n: Note = { sources: [], nodes: [], warbled: [], endsAt: 0 };

    const env = ctx.createGain();
    n.nodes.push(env);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = v.tone;
    lp.Q.value = 0.5;
    n.nodes.push(lp);
    lp.connect(env);
    env.connect(this.tape.input);

    switch (v.kind) {
      case 'fm': {
        const carrier = ctx.createOscillator();
        carrier.type = 'sine';
        carrier.frequency.value = freq;
        carrier.detune.value = cents;
        const mod = ctx.createOscillator();
        mod.type = 'sine';
        mod.frequency.value = freq * (v.ratio ?? 2);
        // Same offset on both, so mistuning the voice does not also change the
        // ratio and with it the timbre.
        mod.detune.value = cents;
        const depth = ctx.createGain();
        // Index scales with frequency so the timbre is constant up the keyboard.
        const peak = freq * (v.index ?? 1.5);
        depth.gain.setValueAtTime(peak, at);
        depth.gain.exponentialRampToValueAtTime(Math.max(1e-3, peak * 0.02), at + (v.modDecay ?? 0.4));
        mod.connect(depth);
        depth.connect(carrier.frequency);
        carrier.connect(lp);
        n.nodes.push(carrier, mod, depth);
        n.sources.push(carrier, mod);
        this.warble(n, carrier.detune);
        this.warble(n, mod.detune);
        break;
      }
      case 'organ': {
        const osc = ctx.createOscillator();
        osc.setPeriodicWave(this.wave(v));
        osc.frequency.value = freq;
        osc.detune.value = cents;
        osc.connect(lp);
        n.nodes.push(osc);
        n.sources.push(osc);
        this.warble(n, osc.detune);
        break;
      }
      default: {
        const count = Math.max(1, v.unison ?? 1);
        const spread = v.spread ?? 0;
        for (let i = 0; i < count; i++) {
          const osc = ctx.createOscillator();
          osc.type = v.wave ?? 'sawtooth';
          osc.frequency.value = freq;
          // Fixed offsets across the stack plus a little jitter: an exactly
          // symmetrical spread beats in a pattern the ear picks up on.
          const detune = count === 1 ? 0 : (i / (count - 1) - 0.5) * 2 * spread + this.rng.range(-2, 2);
          osc.detune.value = detune + cents;
          const trim = ctx.createGain();
          trim.gain.value = 1 / count;
          osc.connect(trim);
          trim.connect(lp);
          n.nodes.push(osc, trim);
          n.sources.push(osc);
          this.warble(n, osc.detune);
        }
        break;
      }
    }

    const end = this.envelope(env.gain, at, dur, level, v);
    for (const s of n.sources) s.start(at);
    for (const s of n.sources) s.stop(end + 0.02);
    n.endsAt = end + REAP_MARGIN;
    this.notes.push(n);
  }

  /**
   * Write the note's amplitude and return the context time it has finished at.
   *
   * A `swell` voice spends its first seconds climbing out of nothing and only
   * then plays its attack, which is a tape running backwards; everything else
   * starts at its attack the way an instrument does.
   */
  private envelope(g: AudioParam, at: number, dur: number, level: number, v: VoiceProfile): number {
    const peak = Math.max(1e-4, level);
    const sus = Math.max(1e-4, level * v.sustain);
    const swell = v.swell ?? 0;
    const hit = at + swell;

    g.setValueAtTime(1e-4, at);
    if (swell > 0) {
      // Exponential, so the first two thirds of the swell are almost inaudible
      // and the note appears to arrive from further away than it started.
      g.exponentialRampToValueAtTime(Math.max(1e-4, peak * 0.45), hit);
    }
    g.linearRampToValueAtTime(peak, hit + v.attack);
    g.exponentialRampToValueAtTime(sus, hit + v.attack + v.decay);

    const off = Math.max(hit + v.attack + v.decay, at + dur);
    g.setValueAtTime(sus, off);
    g.exponentialRampToValueAtTime(1e-4, off + v.release);
    g.setValueAtTime(0, off + v.release);
    return off + v.release;
  }

  private warble(n: Note, param: AudioParam): void {
    this.tape.attach(param);
    n.warbled.push(param);
  }

  private wave(v: VoiceProfile): PeriodicWave {
    let w = this.waves.get(v);
    if (!w) {
      w = makeHarmonicWave(this.ctx, v.partials ?? [1, 0.4, 0.2]);
      this.waves.set(v, w);
    }
    return w;
  }
}

function mtof(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}
